import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { exportSections } from "../../routes/geraetemanagement/exportConfig";
import { netzwirtschaftFieldsData } from "../../routes/netzwirtschaft/netzwirtschaftTypes";
import { nanosecondsTimeStampToLocalString, dateDiffInDaysUTC } from "../../utils/utils";

export const ExportToExcel = (
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiDataArray: { fields: netzwirtschaftFieldsData, data: any[] }[],
  fileName: string
): void => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const wb = XLSX.utils.book_new();

  apiDataArray.forEach((apiData) => {
    const wscols = [
      { width: 30 }, // first column
      { width: 20 }, // second column
      { width: 18 }, // third column
      { width: 30 }, //...
      { width: 15 },
      { width: 30 },
    ];

    let ws = XLSX.utils.aoa_to_sheet([]);

    const headerNames = [["Zeit"].concat(Object.keys(apiData.data[0]).filter(x => x != "zeit").map((x: string) => apiData.fields.messgroesse.find(y => y.feldname == x)?.beschreibung + " (" + x + ")"))];
    XLSX.utils.sheet_add_aoa(ws, headerNames, { origin: "A4" });
    ws = XLSX.utils.sheet_add_json(
      ws,
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      apiData.data.map((e: any) =>
        Object.assign({}, e, { zeit: nanosecondsTimeStampToLocalString(e.zeit) })
      ),
      { skipHeader: true, origin: "A5" }
    );
    ws = XLSX.utils.sheet_add_aoa(ws, [
      [
        "Umspannwerk:",
        apiData.fields.uw,
        "Messpunkt:",
        apiData.fields.messpunkt,
        "Rekorder:",
        apiData.fields.rekorder.beschreibung,
      ],
      [
        "Startzeitpunkt:",
        new Date(apiData.fields.startDatum).toLocaleDateString("de-DE", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
        "Endzeitpunkt:",
        new Date(apiData.fields.endDatum).toLocaleDateString("de-DE", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      ],
      [],
      ["Zeit"],
    ]);
    ws["!cols"] = wscols;

    XLSX.utils.book_append_sheet(wb, ws, "Blatt " + (wb.SheetNames.length + 1));
  });

  const excelBuffer = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
    cellStyles: true,
  });
  const data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(data, fileName + fileExtension);
};

//eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ExportMessstellenToExcel = (data: any[], fileName: string): void => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const modifiedData = data.map(x => {
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _, ...newObject } = x;
    return newObject;
  });

  const headerNames = [
    "Name",
    "Breitengrad",
    "Längengrad",
    "Gerätename",
    "Messfeld",
    "Spannungsebene",
    "Stromwandler",
    "Spannungswandler",
    "Umspannwerk",
    "Kommentar",
    "Aktivem Messpunkt zugeordnet gewesen",
    "Aktivem Messpunkt zugeordnet",
  ];

  let ws = XLSX.utils.aoa_to_sheet([]);
  ws = XLSX.utils.sheet_add_json(ws, modifiedData, { origin: "A1" });
  ws = XLSX.utils.sheet_add_aoa(ws, [headerNames]);
  ws["!cols"] = fitToColumn(modifiedData, headerNames);

  const wb = { Sheets: { Blatt1: ws }, SheetNames: ["Blatt1"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const dataOut = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(dataOut, fileName + fileExtension);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ExportNetzereignisToExcel = (data: any[], fileName: string): void => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const modifiedData = data.map(x => {
    return {
      ereignis: x.ereignisueberschrift, messstelle: x.messstelle, grund: x.grund, freischaltung: x.freischaltung.join(", "), geplant: x.geplant,
      startzeit: x.zeit, endzeit: x.ende, tage: dateDiffInDaysUTC(new Date(x.zeit), new Date(x.ende)), text: x.ereignistext, kommentar: x.kommentar
    };
  });

  const headerNames = [
    "Ereignisüberschrift",
    "Messstelle",
    "Grund der Anforderung",
    "Freischaltung",
    "Geplant",
    "Startzeit",
    "Endzeit",
    "Anzahl Tage",
    "Ereignistext",
    "Kommentar",
  ];

  let ws = XLSX.utils.aoa_to_sheet([]);
  ws = XLSX.utils.sheet_add_json(ws, modifiedData, { origin: "A1" });
  ws = XLSX.utils.sheet_add_aoa(ws, [headerNames]);
  ws["!cols"] = fitToColumn(modifiedData, headerNames);

  const wb = { Sheets: { Blatt1: ws }, SheetNames: ["Blatt1"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const dataOut = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(dataOut, fileName + fileExtension);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ExportRoutergeraeteToExcel = (data: any[], fileName: string): void => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const modifiedData = data.map(x => {
    return {
      seriennummer: x.seriennummer, herstellungsjahr: x.herstellungsjahr, firmware: x.firmware, mac: x.macadresse, inbetriebnahmedatum: x.inbetriebnahmedatum,
      wiv: x.wartungsintervall, zs: x.zeitsynchronisationsart, hersteller: x.rm, typ: x.rtyp, k: x.kommentar, ip: x.ipadresse, submask: x.subnetzmaske, jemalsZugeordnet: x.jemalsZugeordnet, zugeordnet: x.zugeordnet
    };
  });

  const headerNames = [
    "Seriennummer",
    "Herstellungsjahr",
    "Firmware",
    "MAC-Adresse",
    "Inbetriebnahmedatum",
    "Wartungsintervall",
    "Zeitsynchonisationsart",
    "Hersteller",
    "Typ",
    "Kommentar",
    "IP-Adresse",
    "Subnetzmaske",
    "Aktivem Messpunkt zugeordnet gewesen",
    "Aktivem Messpunkt zugeordnet",
  ];

  let ws = XLSX.utils.aoa_to_sheet([]);
  ws = XLSX.utils.sheet_add_json(ws, modifiedData, { origin: "A1" });
  ws = XLSX.utils.sheet_add_aoa(ws, [headerNames]);
  ws["!cols"] = fitToColumn(modifiedData, headerNames);

  const wb = { Sheets: { Blatt1: ws }, SheetNames: ["Blatt1"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const dataOut = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(dataOut, fileName + fileExtension);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ExportMessgeraeteToExcel = (data: any[], fileName: string): void => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const modifiedData = data.map(x => {
    return {
      seriennummer: x.seriennummer, herstellungsjahr: x.herstellungsjahr, firmware: x.firmware, mac: x.macadresse, inbetriebnahmedatum: x.inbetriebnahmedatum,
      wiv: x.wartungsintervall, zs: x.zeitsynchronisationsart, hersteller: x.msm, typ: x.mstyp, k: x.kommentar, ip: x.ipadresse, submask: x.subnetzmaske, jemalsZugeordnet: x.jemalsZugeordnet, zugeordnet: x.zugeordnet
    };
  });

  const headerNames = [
    "Seriennummer",
    "Herstellungsjahr",
    "Firmware",
    "MAC-Adresse",
    "Inbetriebnahmedatum",
    "Wartungsintervall",
    "Zeitsynchonisationsart",
    "Hersteller",
    "Typ",
    "Kommentar",
    "IP-Adresse",
    "Subnetzmaske",
    "Aktivem Messpunkt zugeordnet gewesen",
    "Aktivem Messpunkt zugeordnet",
  ];

  let ws = XLSX.utils.aoa_to_sheet([]);
  ws = XLSX.utils.sheet_add_json(ws, modifiedData, { origin: "A1" });
  ws = XLSX.utils.sheet_add_aoa(ws, [headerNames]);
  ws["!cols"] = fitToColumn(modifiedData, headerNames);

  const wb = { Sheets: { Blatt1: ws }, SheetNames: ["Blatt1"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const dataOut = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(dataOut, fileName + fileExtension);
};

//eslint-disable-next-line @typescript-eslint/no-explicit-any
const fitToColumn = (data: any[], headerNames: string[] = []) => {
  const columnWidths: Array<{ wch: number }> = [];
  let index = 0;
  for (const property in data[0]) {
    columnWidths.push({
      wch: Math.max(
        headerNames.length == Object.keys(data[0]).length ?
          //eslint-disable-next-line security/detect-object-injection
          headerNames[index].length :
          property ? property.toString().length : 0,
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...data.map((obj: any) =>
          //eslint-disable-next-line @typescript-eslint/no-explicit-any
          obj[property as keyof any] ? obj[property as keyof any].toString().length : 0
        )
      )
    });
    index++;
  }
  return columnWidths;
};

//eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ExportAktiveMesspunkteToExcel = (data: any[], selection: Array<string>): void => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  let ws = XLSX.utils.aoa_to_sheet([]);
  ws = XLSX.utils.sheet_add_json(ws, data, { origin: "A1" });
  ws = XLSX.utils.sheet_add_aoa(ws, [
    exportSections.flatMap(e => e.exportColumns.filter(x => selection.includes(x.column)).map(y => e.label + " " + y.label))
  ]);
  ws["!cols"] = fitToColumn(data);


  const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const dataOut = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(dataOut, "aktive_messpunkte" + fileExtension);
};
